import { useLDClient } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { CurrentUserFragment } from 'src/graphql/generated';
import { useUser } from 'src/queries';
import { reportToSentry } from './sentry';
interface ContextProps {
  user: CurrentUserFragment | undefined;
  isLoading: boolean;
}
export const AuthContext = React.createContext<ContextProps | undefined>(undefined);
export interface AuthProviderProps {
  children?: React.ReactNode;
}
export const AuthProvider: React.FC<Readonly<AuthProviderProps>> = ({
  children
}) => {
  const {
    data: user,
    isLoading
  } = useUser();
  const ldClient = useLDClient();
  const [isIdentified, setIsIdentified] = React.useState(false);
  React.useEffect(() => {
    const initDataDogRum = async (user: CurrentUserFragment) => {
      const datadogRum = (await import('@datadog/browser-rum')).datadogRum;
      try {
        const applicationId = process.env.NEXT_PUBLIC_DD_APPLICATION_ID;
        const clientToken = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN;
        const apiUrl = process.env.NEXT_PUBLIC_API_URL;
        if (!applicationId || typeof applicationId !== 'string' || !clientToken || typeof clientToken !== 'string') {
          return;
        }
        datadogRum.init({
          applicationId,
          clientToken,
          env: process.env.NEXT_PUBLIC_DD_ENVIRONMENT,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
          service: process.env.NEXT_PUBLIC_DD_SERVICE_NAME,
          silentMultipleInit: true,
          site: 'datadoghq.com',
          trackUserInteractions: true,
          allowedTracingUrls: apiUrl ? [{
            match: apiUrl,
            propagatorTypes: ['tracecontext']
          }] : [],
          proxy: process.env.NEXT_PUBLIC_DD_FORWARDER_URL
        });
        datadogRum.startSessionReplayRecording();
        datadogRum.setUser({
          id: user.id,
          userRole: user.teamMemberships[0]?.role,
          email: user.email,
          name: user.name ?? '',
          teamId: user.teamMemberships[0]?.team.id,
          teamName: user.teamMemberships[0]?.team.name
        });
      } catch (error) {
        if (error instanceof Error) {
          reportToSentry(error);
        }
      }
    };
    if (user) {
      void initDataDogRum(user);
    }
  }, [user]);
  React.useEffect(() => {
    if (!user) {
      return;
    }
    void ldClient?.identify({
      key: user.id,
      name: user.name ?? '',
      email: user.email,
      custom: {
        teamId: user.teamMemberships[0]?.team.id ?? ''
      }
    }).then(() => setIsIdentified(true));
  }, [user, ldClient]);
  return <AuthContext.Provider value={{
    // Wait for LD to identify the user before returning the user object
    user: isIdentified ? user : undefined,
    isLoading: isLoading || (user ? !isIdentified : false)
  }} data-sentry-element="AuthContext.Provider" data-sentry-component="AuthProvider" data-sentry-source-file="auth-context.tsx">
      {children}
    </AuthContext.Provider>;
};
export function useAuth(): ContextProps {
  const authContext = React.useContext(AuthContext);
  if (!authContext) {
    throw new TypeError('`useAuth` must be called from within an `AuthProvider`');
  }
  return authContext;
}